import React, { Fragment, useState, useEffect, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { WEBSERVICE, SITENAMEALIAS } from "../utils/init";
import { showToast } from "../utils/library";
import { getAuthToken, getAuthUuid } from "../utils/service";

const LIMIT = 10;

// ─────────────────────────────────────────────────────────────────
// CSS
// ─────────────────────────────────────────────────────────────────
const CSS = `
.ec-page { padding: 32px 24px 80px; max-width: 100%; overflow-x: hidden; }
.ec-hdr { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 24px; gap: 16px; flex-wrap: wrap; }
.ec-hdr-left { display: flex; flex-direction: column; gap: 4px; }
.ec-title { font-size: 22px; font-weight: 700; letter-spacing: -.4px; color: var(--text-color); margin: 0; }
.ec-sub { font-size: 13px; color: var(--text-color); opacity: .65; margin: 0; }
.ec-hdr-right { display: flex; align-items: center; gap: 10px; }

.ec-role-chip { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .6px; border: 1px solid; }
.ec-role-chip.payer { background: color-mix(in srgb, var(--btn-color) 10%, transparent); border-color: color-mix(in srgb, var(--btn-color) 28%, transparent); color: var(--btn-color); }
.ec-role-chip.payee { background: rgba(16, 185, 129, .1); border-color: rgba(16, 185, 129, .28); color: #10b981; }
.ec-role-chip.approval { background: rgba(217, 119, 6, .1); border-color: rgba(217, 119, 6, .28); color: #d97706; }

.ec-btn-create { height: 36px; padding: 0 16px; border-radius: 8px; font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; transition: all .15s; white-space: nowrap; border: none; background: var(--btn-color); color: #fff; box-shadow: 0 0 14px color-mix(in srgb, var(--btn-color) 30%, transparent); }
.ec-btn-create:hover { filter: brightness(1.1); transform: scale(.98); }

.ec-topbar { display: flex; gap: 10px; align-items: center; margin-bottom: 16px; flex-wrap: wrap; }
.ec-search-wrap { flex: 1; min-width: 180px; position: relative; }
.ec-search-icon { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: var(--text-color); opacity: .5; pointer-events: none; }
.ec-search-input { width: 100%; height: 36px; padding: 0 12px 0 34px; background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 8px; color: var(--text-color); font-family: inherit; font-size: 13px; outline: none; transition: border-color .2s; }
.ec-search-input::placeholder { color: var(--text-color); opacity: .5; }
.ec-search-input:focus { border-color: var(--btn-color); box-shadow: 0 0 0 3px color-mix(in srgb, var(--btn-color) 12%, transparent); }
.ec-filter-sel { height: 36px; padding: 0 12px; background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 8px; color: var(--text-color); font-family: inherit; font-size: 13px; appearance: auto; cursor: pointer; outline: none; }
.ec-filter-sel:focus { border-color: var(--btn-color); }

.ec-tbl-wrap { background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 12px; overflow-x: auto; -webkit-overflow-scrolling: touch; }
.ec-tbl-wrap table { width: 100%; border-collapse: collapse; min-width: 700px; }
.ec-tbl-wrap thead tr { border-bottom: 1px solid var(--body-color-less2); }
.ec-tbl-wrap thead th { padding: 12px 16px; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .65px; color: var(--text-color); opacity: .7; text-align: left; white-space: nowrap; }
.ec-tbl-wrap tbody tr { border-bottom: 1px solid var(--body-color-less2); transition: background .15s; }
.ec-tbl-wrap tbody tr:last-child { border-bottom: none; }
.ec-tbl-wrap tbody tr:hover { background: color-mix(in srgb, var(--btn-color) 4%, transparent); }
.ec-tbl-wrap tbody td { padding: 13px 16px; font-size: 13px; vertical-align: middle; text-align: left; }
.ec-td-name { font-weight: 600; color: var(--text-color); }
.ec-td-sub { font-size: 11px; color: var(--text-color); opacity: .6; margin-top: 2px; }
.ec-td-amount { font-weight: 700; color: var(--text-color); font-size: 14px; }
.ec-td-actions { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }

.ec-badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 600; }
.ec-badge::before { content: ''; width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
/* Legacy status classes */
.ec-badge.funded { background: rgba(16, 185, 129, .15); color: #10b981; }
.ec-badge.funded::before { background: #10b981; }
.ec-badge.unfunded { background: rgba(100, 116, 139, .15); color: #94a3b8; }
.ec-badge.unfunded::before { background: #94a3b8; }
.ec-badge.in_dispute { background: rgba(217, 119, 6, .13); color: #d97706; }
.ec-badge.in_dispute::before { background: #d97706; }
.ec-badge.released { background: rgba(37, 99, 235, .12); color: #60a5fa; }
.ec-badge.released::before { background: #60a5fa; }
.ec-badge.cancelled { background: rgba(239, 68, 68, .1); color: #f87171; }
.ec-badge.cancelled::before { background: #f87171; }
/* API status classes */
.ec-badge.save-draft { background: rgba(100, 116, 139, .15); color: #94a3b8; }
.ec-badge.save-draft::before { background: #94a3b8; }
.ec-badge.created { background: color-mix(in srgb, var(--btn-color) 12%, transparent); color: var(--btn-color); }
.ec-badge.created::before { background: var(--btn-color); }
.ec-badge.active { background: rgba(16, 185, 129, .15); color: #10b981; }
.ec-badge.active::before { background: #10b981; }
.ec-badge.completed { background: rgba(37, 99, 235, .12); color: #60a5fa; }
.ec-badge.completed::before { background: #60a5fa; }
.ec-badge.pending { background: rgba(217, 119, 6, .13); color: #d97706; }
.ec-badge.pending::before { background: #d97706; }
.ec-badge.rejected { background: rgba(239, 68, 68, .1); color: #f87171; }
.ec-badge.rejected::before { background: #f87171; }
.ec-badge.in-dispute { background: rgba(217, 119, 6, .13); color: #d97706; }
.ec-badge.in-dispute::before { background: #d97706; }
.ec-badge.disputed { background: rgba(239, 68, 68, .1); color: #f87171; }
.ec-badge.disputed::before { background: #f87171; }
.ec-badge.in-review { background: rgba(139, 92, 246, .12); color: #a78bfa; }
.ec-badge.in-review::before { background: #a78bfa; }
.ec-badge.release-approved { background: rgba(16, 185, 129, .15); color: #10b981; }
.ec-badge.release-approved::before { background: #10b981; }
.ec-badge.fund-initiated { background: rgba(245, 158, 11, .12); color: #f59e0b; }
.ec-badge.fund-initiated::before { background: #f59e0b; }
.ec-badge.partially-funded { background: rgba(234, 88, 12, .12); color: #ea580c; }
.ec-badge.partially-funded::before { background: #ea580c; }
.ec-badge.funding-rejected { background: rgba(239, 68, 68, .1); color: #f87171; }
.ec-badge.funding-rejected::before { background: #f87171; }

/* ── Action buttons ── */
.ec-btn-view    { background: color-mix(in srgb, var(--btn-color) 12%, transparent); border: 1px solid color-mix(in srgb, var(--btn-color) 25%, transparent); color: var(--btn-color); }
.ec-btn-view:hover { background: color-mix(in srgb, var(--btn-color) 22%, transparent); }
.ec-btn-fund    { background: rgba(5, 150, 105, .12); border: 1px solid rgba(5, 150, 105, .3); color: #059669; }
.ec-btn-fund:hover { background: rgba(5, 150, 105, .22); }
.ec-btn-dispute { background: rgba(217, 119, 6, .1); border: 1px solid rgba(217, 119, 6, .28); color: #d97706; }
.ec-btn-dispute:hover { background: rgba(217, 119, 6, .2); }
.ec-btn-cancel  { background: rgba(239, 68, 68, .08); border: 1px solid rgba(239, 68, 68, .25); color: #f87171; }
.ec-btn-cancel:hover { background: rgba(239, 68, 68, .18); }
.ec-btn-review  { background: color-mix(in srgb, var(--btn-color) 12%, transparent); border: 1px solid color-mix(in srgb, var(--btn-color) 25%, transparent); color: var(--btn-color); }
.ec-btn-review:hover { background: color-mix(in srgb, var(--btn-color) 22%, transparent); }
.ec-btn-request { background: rgba(37, 99, 235, .1); border: 1px solid rgba(37, 99, 235, .28); color: #60a5fa; }
.ec-btn-request:hover { background: rgba(37, 99, 235, .2); }
.ec-btn-respond { background: rgba(217, 119, 6, .1); border: 1px solid rgba(217, 119, 6, .28); color: #d97706; }
.ec-btn-respond:hover { background: rgba(217, 119, 6, .2); }
.ec-btn-approve { background: rgba(16, 185, 129, .12); border: 1px solid rgba(16, 185, 129, .3); color: #10b981; }
.ec-btn-approve:hover { background: rgba(16, 185, 129, .22); }
.ec-btn-reject  { background: rgba(239, 68, 68, .08); border: 1px solid rgba(239, 68, 68, .25); color: #f87171; }
.ec-btn-reject:hover { background: rgba(239, 68, 68, .18); }
.ec-btn-withdraw { background: rgba(37, 99, 235, .1); border: 1px solid rgba(37, 99, 235, .28); color: #60a5fa; }
.ec-btn-withdraw:hover { background: rgba(37, 99, 235, .2); }

/* shared action btn base */
.ec-action-btn { height: 30px; padding: 0 12px; font-size: 12px; border-radius: 6px; font-family: inherit; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; transition: all .15s; white-space: nowrap; }

.ec-empty { display: flex; flex-direction: column; align-items: center; padding: 60px 20px; gap: 14px; text-align: center; }
.ec-empty-icon { font-size: 36px; opacity: .3; }
.ec-empty-text { font-size: 14px; color: var(--text-color); opacity: .6; }

/* Loading skeleton */
.ec-skeleton-row td { padding: 13px 16px; }
.ec-skeleton-line { height: 12px; border-radius: 6px; background: linear-gradient(90deg, var(--body-color-less2) 25%, color-mix(in srgb, var(--body-color-less2) 60%, transparent) 50%, var(--body-color-less2) 75%); background-size: 200% 100%; animation: ec-shimmer 1.4s infinite; }
.ec-skeleton-line.short { width: 60%; }
.ec-skeleton-line.xshort { width: 40%; margin-top: 5px; }
@keyframes ec-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

/* Error state */
.ec-error { display: flex; flex-direction: column; align-items: center; padding: 40px 20px; gap: 10px; text-align: center; }
.ec-error-icon { font-size: 28px; color: #f87171; opacity: .7; }
.ec-error-text { font-size: 13px; color: #f87171; }
.ec-retry-btn { margin-top: 4px; height: 32px; padding: 0 16px; border-radius: 7px; border: 1px solid rgba(239,68,68,.3); background: rgba(239,68,68,.08); color: #f87171; font-family: inherit; font-size: 12px; cursor: pointer; transition: all .15s; }
.ec-retry-btn:hover { background: rgba(239,68,68,.16); }

/* Pagination */
.ec-pagination { display: flex; align-items: center; justify-content: flex-end; gap: 6px; margin-top: 16px; flex-wrap: wrap; }
.ec-page-btn { height: 34px; min-width: 34px; padding: 0 12px; border-radius: 7px; border: 1px solid var(--body-color-less2); background: var(--body-color-less); color: var(--text-color); font-family: inherit; font-size: 13px; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 6px; transition: all .15s; white-space: nowrap; }
.ec-page-btn:hover:not(:disabled) { border-color: var(--btn-color); color: var(--btn-color); }
.ec-page-btn:disabled { opacity: .38; cursor: not-allowed; }
.ec-page-btn.active { background: var(--btn-color); border-color: var(--btn-color); color: #fff; font-weight: 600; }
.ec-page-info { font-size: 13px; color: var(--text-color); opacity: .6; padding: 0 4px; }

.ec-confirm-bg { position: fixed; inset: 0; background: rgba(0,0,0,.65); backdrop-filter: blur(4px); z-index: 1000; display: flex; align-items: center; justify-content: center; opacity: 0; pointer-events: none; transition: opacity .2s; }
.ec-confirm-bg.open { opacity: 1; pointer-events: all; }
.ec-confirm-box { background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 14px; padding: 24px; width: 360px; max-width: 90vw; transform: translateY(12px); transition: transform .2s; }
.ec-confirm-bg.open .ec-confirm-box { transform: translateY(0); }
.ec-confirm-title { font-size: 16px; font-weight: 700; color: var(--text-color); margin: 0 0 8px; }
.ec-confirm-msg { font-size: 13px; color: var(--text-color); opacity: .7; margin: 0 0 20px; line-height: 1.55; }
.ec-confirm-actions { display: flex; justify-content: flex-end; gap: 10px; }
.ec-confirm-cancel-btn { height: 36px; padding: 0 18px; border-radius: 8px; border: 1px solid var(--body-color-less2); background: transparent; color: var(--text-color); opacity: .8; font-family: inherit; font-size: 13px; font-weight: 500; cursor: pointer; transition: all .15s; }
.ec-confirm-cancel-btn:hover { opacity: 1; border-color: var(--text-color); }
.ec-confirm-ok-btn { height: 36px; padding: 0 18px; border-radius: 8px; border: none; background: var(--btn-color); color: #fff; font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer; transition: filter .15s; }
.ec-confirm-ok-btn:hover { filter: brightness(1.1); }
.ec-confirm-ok-btn.danger { background: #ef4444; }

@keyframes ec-fade-up { from { opacity: 0; transform: translateY(7px); } to { opacity: 1; transform: translateY(0); } }
.ec-view-in { animation: ec-fade-up .2s ease; }

/* ── Cancel Modal ── */
.ec-cancel-bg { position: fixed; inset: 0; background: rgba(0,0,0,.65); backdrop-filter: blur(4px); z-index: 1000; display: flex; align-items: center; justify-content: center; opacity: 0; pointer-events: none; transition: opacity .2s; }
.ec-cancel-bg.open { opacity: 1; pointer-events: all; }
.ec-cancel-box { background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 14px; padding: 24px; width: 440px; max-width: 92vw; transform: translateY(12px); transition: transform .2s; }
.ec-cancel-bg.open .ec-cancel-box { transform: translateY(0); }
.ec-cancel-title { font-size: 16px; font-weight: 700; color: var(--text-color); margin: 0 0 4px; }
.ec-cancel-sub { font-size: 12px; color: var(--text-color); opacity: .6; margin: 0 0 18px; }
.ec-cancel-label { display: block; font-size: 12px; font-weight: 600; color: var(--text-color); opacity: .75; margin-bottom: 5px; }
.ec-cancel-textarea { width: 100%; min-height: 80px; background: var(--body-color-less); border: 1.5px solid var(--body-color-less2); border-radius: 8px; padding: 10px 12px; color: var(--text-color); font-family: inherit; font-size: 13px; resize: vertical; outline: none; box-sizing: border-box; transition: border-color .2s; }
.ec-cancel-textarea:focus { border-color: #ef4444; }
.ec-cancel-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 20px; }
.ec-cancel-close-btn { height: 36px; padding: 0 18px; border-radius: 8px; border: 1px solid var(--body-color-less2); background: transparent; color: var(--text-color); opacity: .8; font-family: inherit; font-size: 13px; font-weight: 500; cursor: pointer; transition: all .15s; }
.ec-cancel-close-btn:hover { opacity: 1; border-color: var(--text-color); }
.ec-cancel-submit-btn { height: 36px; padding: 0 18px; border-radius: 8px; border: none; background: #ef4444; color: #fff; font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer; transition: filter .15s; display: inline-flex; align-items: center; gap: 7px; }
.ec-cancel-submit-btn:hover:not(:disabled) { filter: brightness(1.1); }
.ec-cancel-submit-btn:disabled { opacity: .55; cursor: not-allowed; }

@media (max-width: 700px) {
  .ec-page { padding: 16px 10px 80px; }
  .ec-topbar { flex-direction: column; }
  .ec-td-actions { gap: 4px; }
  .ec-hdr { flex-direction: column; }
  .ec-pagination { justify-content: center; }
}

/* ── Dispute Modal ── */
.ec-dispute-bg { position: fixed; inset: 0; background: rgba(0,0,0,.65); backdrop-filter: blur(4px); z-index: 1000; display: flex; align-items: center; justify-content: center; opacity: 0; pointer-events: none; transition: opacity .2s; }
.ec-dispute-bg.open { opacity: 1; pointer-events: all; }
.ec-dispute-box { background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 14px; padding: 24px; width: 440px; max-width: 92vw; transform: translateY(12px); transition: transform .2s; }
.ec-dispute-bg.open .ec-dispute-box { transform: translateY(0); }
.ec-dispute-title { font-size: 16px; font-weight: 700; color: var(--text-color); margin: 0 0 4px; }
.ec-dispute-sub { font-size: 12px; color: var(--text-color); opacity: .6; margin: 0 0 18px; }
.ec-dispute-label { display: block; font-size: 12px; font-weight: 600; color: var(--text-color); opacity: .75; margin-bottom: 5px; }
.ec-dispute-textarea { width: 100%; min-height: 90px; background: var(--body-color-less); border: 1.5px solid var(--body-color-less2); border-radius: 8px; padding: 10px 12px; color: var(--text-color); font-family: inherit; font-size: 13px; resize: vertical; outline: none; box-sizing: border-box; transition: border-color .2s; }
.ec-dispute-textarea:focus { border-color: var(--btn-color); }
.ec-dispute-file-label { display: flex; align-items: center; gap: 8px; height: 38px; padding: 0 14px; border: 1.5px dashed var(--body-color-less2); border-radius: 8px; cursor: pointer; font-size: 12px; color: var(--text-color); opacity: .7; transition: all .15s; margin-top: 14px; }
.ec-dispute-file-label:hover { border-color: var(--btn-color); opacity: 1; color: var(--btn-color); }
.ec-dispute-file-label-error { border-color: rgba(239,68,68,.55) !important; color: #f87171 !important; opacity: 1 !important; }
.ec-dispute-file-name { font-size: 11px; color: var(--text-color); opacity: .6; margin-top: 5px; }
.ec-dispute-file-error { font-size: 11px; color: #f87171; margin-top: 5px; display: flex; align-items: center; gap: 4px; }
.ec-dispute-file-hint { font-size: 11px; color: var(--text-color); opacity: .45; margin-top: 5px; }
.ec-dispute-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 20px; }
.ec-dispute-cancel-btn { height: 36px; padding: 0 18px; border-radius: 8px; border: 1px solid var(--body-color-less2); background: transparent; color: var(--text-color); opacity: .8; font-family: inherit; font-size: 13px; font-weight: 500; cursor: pointer; transition: all .15s; }
.ec-dispute-cancel-btn:hover { opacity: 1; border-color: var(--text-color); }
.ec-dispute-submit-btn { height: 36px; padding: 0 18px; border-radius: 8px; border: none; background: #d97706; color: #fff; font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer; transition: filter .15s; display: inline-flex; align-items: center; gap: 7px; }
.ec-dispute-submit-btn:hover:not(:disabled) { filter: brightness(1.1); }
.ec-dispute-submit-btn:disabled { opacity: .55; cursor: not-allowed; }
`;

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────
const STATUS_LABELS = {
  SAVE_DRAFT: "Draft",
  CREATED: "Created",
  ACTIVE: "Active",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
  PENDING: "Pending",
  REJECTED: "Rejected",
  IN_DISPUTE: "In Dispute",
  DISPUTED: "Disputed",
  IN_REVIEW: "In Review",
  RELEASE_APPROVED: "Release Approved",
  RELEASED: "Released",
  FUNDED: "Funded",
  UNFUNDED: "Unfunded",
  FUND_INITIATED: "Fund Initiated",
  PARTIALLY_FUNDED: "Partially Funded",
  FUNDING_REJECTED: "Fund Rejected",
};

const STATUS_CSS = {
  SAVE_DRAFT: "save-draft",
  CREATED: "created",
  ACTIVE: "active",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  PENDING: "pending",
  REJECTED: "rejected",
  IN_DISPUTE: "in-dispute",
  DISPUTED: "disputed",
  IN_REVIEW: "in-review",
  RELEASE_APPROVED: "release-approved",
  RELEASED: "released",
  FUNDED: "funded",
  UNFUNDED: "unfunded",
  FUND_INITIATED: "fund-initiated",
  PARTIALLY_FUNDED: "partially-funded",
  FUNDING_REJECTED: "funding-rejected",
};

const ROLE_LABELS = { payer: "Payer", payee: "Payee", approval: "Approver" };
const ROLE_ICONS  = { payer: "fa-user", payee: "fa-handshake", approval: "fa-gavel" };

const PAGE_TITLES = {
  payer:    { title: "Escrow Contracts", sub: "Contracts you have initiated as a payer" },
  payee:    { title: "Escrow Contracts", sub: "Contracts where you are the payee" },
  approval: { title: "Escrow Contracts", sub: "Contracts pending review or approval" },
};

function StatusBadge({ status }) {
  const cssClass = STATUS_CSS[status] || (status || "").toLowerCase().replace(/_/g, "-");
  return (
    <span className={`ec-badge ${cssClass}`}>
      {STATUS_LABELS[status] || status || "—"}
    </span>
  );
}

function fmtDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

function fmtAmount(amount) {
  if (amount === null || amount === undefined) return "—";
  return Number(amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getParticipantByRole(participants, role) {
  return (participants || []).find((p) => p.role === role) || null;
}

function getParticipantsByRole(participants, role) {
  return (participants || []).filter((p) => p.role === role);
}

function ParticipantList({ participants }) {
  if (!participants || participants.length === 0) {
    return <span style={{ color: "var(--text-color)", opacity: 0.45 }}>—</span>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {participants.map((p, i) => (
        <div key={p.participantId || i}>
          <div className="ec-td-name">{p.name || "—"}</div>
          <div className="ec-td-sub">{p.email || ""}</div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Confirm Dialog
// ─────────────────────────────────────────────────────────────────
function ConfirmDialog({ open, title, message, confirmLabel, danger, onConfirm, onCancel }) {
  return (
    <div className={`ec-confirm-bg${open ? " open" : ""}`}>
      <div className="ec-confirm-box">
        <p className="ec-confirm-title">{title}</p>
        <p className="ec-confirm-msg">{message}</p>
        <div className="ec-confirm-actions">
          <button className="ec-confirm-cancel-btn" onClick={onCancel}>Cancel</button>
          <button
            className={`ec-confirm-ok-btn${danger ? " danger" : ""}`}
            onClick={onConfirm}
          >
            {confirmLabel || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Table head per role
// ─────────────────────────────────────────────────────────────────
function TableHead({ role }) {
  if (role === "payer") {
    return (
      <thead>
        <tr>
          <th>Contract</th>
          <th>Amount</th>
          <th>Payee</th>
          <th>Contract Status</th>
          <th>Funding Status</th>
          <th>Created</th>
          <th>Action</th>
        </tr>
      </thead>
    );
  }
  if (role === "payee") {
    return (
      <thead>
        <tr>
          <th>Contract</th>
          <th>Amount</th>
          <th>Payer</th>
          <th>Contract Status</th>
          <th>Funding Status</th>
          <th>Created</th>
          <th>Action</th>
        </tr>
      </thead>
    );
  }
  return (
    <thead>
      <tr>
        <th>Contract</th>
        <th>Amount</th>
        <th>Payer</th>
        <th>Payee</th>
        <th>Contract Status</th>
        <th>Funding Status</th>
        <th>Action</th>
      </tr>
    </thead>
  );
}

// ─────────────────────────────────────────────────────────────────
// Action cells per role
// ─────────────────────────────────────────────────────────────────
function PayerActions({ contract, onView, onFund, onCancel, onEdit, merchantId }) {
  const { status } = contract;
  const isCreator = parseInt(merchantId, 10) === contract.createdBy;
  return (
    <div className="ec-td-actions">
      {status !== "SAVE_DRAFT" && (
        <button className="ec-action-btn ec-btn-view" onClick={() => onView(contract)}>
          <i className="fas fa-eye" style={{ fontSize: 11 }} /> View
        </button>
      )}
      {status === "SAVE_DRAFT" && isCreator && (
        <button className="ec-action-btn ec-btn-review" onClick={() => onEdit(contract)}>
          <i className="fas fa-pencil-alt" style={{ fontSize: 11 }} /> Edit Contract
        </button>
      )}
      {(status === "CREATED" ||
        (contract.fundingStatus || "").toUpperCase() === "PARTIALLY_FUNDED" ||
        (contract.fundingStatus || "").toUpperCase() === "PENDING") && (
        <button className="ec-action-btn ec-btn-fund" onClick={() => onFund(contract)}>
          <i className="fas fa-wallet" style={{ fontSize: 11 }} /> Fund Escrow
        </button>
      )}
      {(status === "CREATED" || status === "SAVE_DRAFT") && (
        <button className="ec-action-btn ec-btn-cancel" onClick={() => onCancel(contract)}>
          <i className="fas fa-times" style={{ fontSize: 11 }} /> Cancel
        </button>
      )}
    </div>
  );
}

function PayeeActions({ contract, onView, onDispute, onWithdraw, onEdit, merchantId }) {
  const { status } = contract;
  const isFunded = status === "FUNDED" || (contract.fundingStatus || "").toUpperCase() === "FUNDED";
  const isCreator = parseInt(merchantId, 10) === contract.createdBy;
  return (
    <div className="ec-td-actions">
      {status !== "SAVE_DRAFT" && (
        <button className="ec-action-btn ec-btn-view" onClick={() => onView(contract)}>
          <i className="fas fa-eye" style={{ fontSize: 11 }} /> View
        </button>
      )}
      {status === "SAVE_DRAFT" && isCreator && (
        <button className="ec-action-btn ec-btn-review" onClick={() => onEdit(contract)}>
          <i className="fas fa-pencil-alt" style={{ fontSize: 11 }} /> Edit Contract
        </button>
      )}
      {isFunded && (
        <button className="ec-action-btn ec-btn-dispute" onClick={() => onDispute(contract)}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: 11 }} /> Dispute
        </button>
      )}
      {(status === "RELEASED" || status === "RELEASE_APPROVED") && (
        <button className="ec-action-btn ec-btn-withdraw" onClick={() => onWithdraw(contract)}>
          <i className="fas fa-arrow-circle-down" style={{ fontSize: 11 }} /> Withdraw
        </button>
      )}
    </div>
  );
}

function ApprovalActions({ contract, onReview, onApprove, onReject, onEdit, merchantId }) {
  const { status } = contract;
  const isCreator = parseInt(merchantId, 10) === contract.createdBy;
  return (
    <div className="ec-td-actions">
      {status !== "SAVE_DRAFT" && (
        <button className="ec-action-btn ec-btn-review" onClick={() => onReview(contract)}>
          <i className="fas fa-search" style={{ fontSize: 11 }} /> Review
        </button>
      )}
      {status === "SAVE_DRAFT" && isCreator && (
        <button className="ec-action-btn ec-btn-review" onClick={() => onEdit(contract)}>
          <i className="fas fa-pencil-alt" style={{ fontSize: 11 }} /> Edit Contract
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Pagination
// ─────────────────────────────────────────────────────────────────
function Pagination({ currentPage, hasMore, loading, onPageChange }) {
  return (
    <div className="ec-pagination">
      <button
        className="ec-page-btn"
        disabled={currentPage === 1 || loading}
        onClick={() => onPageChange(currentPage - 1)}
      >
        <i className="fas fa-chevron-left" style={{ fontSize: 11 }} /> Prev
      </button>
      <span className="ec-page-info">Page {currentPage}</span>
      <button
        className="ec-page-btn"
        disabled={!hasMore || loading}
        onClick={() => onPageChange(currentPage + 1)}
      >
        Next <i className="fas fa-chevron-right" style={{ fontSize: 11 }} />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Skeleton rows while loading
// ─────────────────────────────────────────────────────────────────
function SkeletonRows({ colSpan }) {
  return Array.from({ length: 5 }).map((_, i) => (
    <tr key={i} className="ec-skeleton-row">
      <td>
        <div className="ec-skeleton-line" />
        <div className="ec-skeleton-line xshort" />
      </td>
      <td><div className="ec-skeleton-line short" /></td>
      <td><div className="ec-skeleton-line short" /></td>
      {colSpan > 6 && <td><div className="ec-skeleton-line short" /></td>}
      <td><div className="ec-skeleton-line short" /></td>
      <td><div className="ec-skeleton-line short" /></td>
      <td><div className="ec-skeleton-line short" /></td>
    </tr>
  ));
}

// ─────────────────────────────────────────────────────────────────
// Dispute Modal
// ─────────────────────────────────────────────────────────────────
const DISPUTE_ALLOWED_TYPES = ["image/jpeg", "image/png", "application/pdf"];
const DISPUTE_ALLOWED_EXT   = ".jpg, .jpeg, .png, .pdf";
const DISPUTE_MAX_MB        = 5;
const DISPUTE_MAX_B         = DISPUTE_MAX_MB * 1024 * 1024;

function DisputeModal({ open, contractNumericId, raisedBy, contractTitle, onClose, onSuccess }) {
  const [description, setDescription] = useState("");
  const [file, setFile]               = useState(null);
  const [fileError, setFileError]     = useState("");
  const [submitting, setSubmitting]   = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) { setDescription(""); setFile(null); setFileError(""); setSubmitting(false); }
  }, [open]);

  const handleFileChange = (e) => {
    const selected = e.target.files[0] || null;
    if (!selected) { setFile(null); setFileError(""); return; }
    if (!DISPUTE_ALLOWED_TYPES.includes(selected.type)) {
      setFile(null);
      setFileError(`Invalid file type. Allowed: ${DISPUTE_ALLOWED_EXT}`);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (selected.size > DISPUTE_MAX_B) {
      setFile(null);
      setFileError(`File exceeds ${DISPUTE_MAX_MB} MB limit.`);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setFile(selected);
    setFileError("");
  };

  const handleSubmit = async () => {
    if (!description.trim()) { showToast("error", "Please enter a description."); return; }
    if (fileError) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("contractId",  contractNumericId);
      fd.append("raisedBy",    raisedBy);
      fd.append("description", description.trim());
      fd.append("milestoneId", "");
      if (file) fd.append("evidenceFile", file);

      const res = await fetch(`${WEBSERVICE}/escrow/raise`, {
        method: "POST",
        headers: {
          Authorization: "bearer " + getAuthToken(),
          UUID: getAuthUuid(),
        },
        body: fd,
      });
      const data = await res.json();
      if (data.status === true) {
        showToast("success", data.message || "Dispute raised successfully.");
        onSuccess();
        onClose();
      } else {
        showToast("error", data.message || "Failed to raise dispute.");
      }
    } catch {
      showToast("error", "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`ec-dispute-bg${open ? " open" : ""}`} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ec-dispute-box">
        <p className="ec-dispute-title">
          <i className="fas fa-exclamation-triangle" style={{ marginRight: 8, color: "#d97706" }} />
          Open Dispute
        </p>
        <p className="ec-dispute-sub">{contractTitle ? `"${contractTitle}"` : ""}</p>

        <label className="ec-dispute-label">
          Description <span style={{ color: "#f87171" }}>*</span>
        </label>
        <textarea
          className="ec-dispute-textarea"
          placeholder="Describe the issue in detail…"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={submitting}
        />

        <label className="ec-dispute-label" style={{ marginTop: 14 }}>
          Evidence File <span style={{ opacity: .5 }}>(optional)</span>
        </label>
        <label className={`ec-dispute-file-label${fileError ? " ec-dispute-file-label-error" : ""}`}>
          <i className="fas fa-paperclip" style={{ fontSize: 12 }} />
          {file ? file.name : "Attach a file…"}
          <input
            ref={fileRef}
            type="file"
            accept=".jpg,.jpeg,.png,.pdf"
            style={{ display: "none" }}
            onChange={handleFileChange}
            disabled={submitting}
          />
        </label>
        {fileError ? (
          <div className="ec-dispute-file-error">
            <i className="fas fa-exclamation-circle" style={{ fontSize: 11 }} /> {fileError}
          </div>
        ) : file ? (
          <div className="ec-dispute-file-name">
            {file.name}
            <span
              style={{ marginLeft: 8, cursor: "pointer", color: "#f87171" }}
              onClick={() => { setFile(null); setFileError(""); if (fileRef.current) fileRef.current.value = ""; }}
            >
              <i className="fas fa-times" />
            </span>
          </div>
        ) : (
          <div className="ec-dispute-file-hint">
            Accepted: {DISPUTE_ALLOWED_EXT} &nbsp;·&nbsp; Max {DISPUTE_MAX_MB} MB
          </div>
        )}

        <div className="ec-dispute-actions">
          <button className="ec-dispute-cancel-btn" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="ec-dispute-submit-btn" onClick={handleSubmit} disabled={submitting}>
            {submitting
              ? <><i className="fas fa-spinner fa-spin" style={{ fontSize: 11 }} /> Submitting…</>
              : <><i className="fas fa-exclamation-triangle" style={{ fontSize: 11 }} /> Open Dispute</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Cancel Modal
// ─────────────────────────────────────────────────────────────────
function CancelModal({ open, contract, merchantId, onClose, onSuccess }) {
  const [reason, setReason]       = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) { setReason(""); setSubmitting(false); }
  }, [open]);

  const handleSubmit = async () => {
    if (!reason.trim()) { showToast("error", "Please enter a cancellation reason."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${WEBSERVICE}/escrow/cancel-escrow-contract`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + getAuthToken(),
          UUID: getAuthUuid(),
        },
        body: JSON.stringify({
          contractId: contract.contractId,
          cancelReason: reason.trim(),
          userId: parseInt(merchantId, 10),
        }),
      });
      const data = await res.json();
      if (data.status === true) {
        showToast("success", data.message || "Contract cancelled successfully.");
        onSuccess();
        onClose();
      } else {
        showToast("error", data.message || "Failed to cancel contract.");
      }
    } catch {
      showToast("error", "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`ec-cancel-bg${open ? " open" : ""}`} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ec-cancel-box">
        <p className="ec-cancel-title">
          <i className="fas fa-times-circle" style={{ marginRight: 8, color: "#ef4444" }} />
          Cancel Contract
        </p>
        <p className="ec-cancel-sub">{contract ? `"${contract.contractTitle || contract.contractNo}"` : ""}</p>

        <label className="ec-cancel-label">
          Cancellation Reason <span style={{ color: "#f87171" }}>*</span>
        </label>
        <textarea
          className="ec-cancel-textarea"
          placeholder="Provide a reason for cancellation…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
        />

        <div className="ec-cancel-actions">
          <button className="ec-cancel-close-btn" onClick={onClose} disabled={submitting}>Back</button>
          <button className="ec-cancel-submit-btn" onClick={handleSubmit} disabled={submitting}>
            {submitting
              ? <><i className="fas fa-spinner fa-spin" style={{ fontSize: 11 }} /> Cancelling…</>
              : <><i className="fas fa-times-circle" style={{ fontSize: 11 }} /> Cancel Contract</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────
export default function EscrowContract() {
  const { userType } = useParams();
  const history = useHistory();

  const role = ["payer", "payee", "approval"].includes(userType) ? userType : "payer";
  const apiRole = role === "approval" ? "APPROVER" : role.toUpperCase();
  const { title, sub } = PAGE_TITLES[role];
  const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");

  const [search, setSearch]           = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [contracts, setContracts]     = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore]         = useState(false);
  const [confirm, setConfirm]         = useState(null);
  const [cancelModal, setCancelModal]   = useState(null); // { contract }
  const [disputeModal, setDisputeModal] = useState(null); // { contract }
  const [refreshKey, setRefreshKey]   = useState(0);

  const debounceTimer = useRef(null);

  // ── Debounce search input ──────────────────────────────────────
  useEffect(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(debounceTimer.current);
  }, [search]);

  // ── Fetch contracts from API ───────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const offset = (currentPage - 1) * LIMIT;
    const merchantId = parseInt(localStorage.getItem(SITENAMEALIAS + "_merchant_id"), 10);

    setLoading(true);
    setError(null);

    fetch(`${WEBSERVICE}/escrow/getEscrowContracts`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "bearer " + getAuthToken(),
        UUID: getAuthUuid(),
      },
      body: JSON.stringify({
        merchantId,
        status: statusFilter,
        role: apiRole,
        offset,
        limit: LIMIT,
        searchString: debouncedSearch,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (data.status === false) {
          showToast("error", data.message || "Something went wrong. Please try again.");
          setContracts([]);
          setHasMore(false);
          setLoading(false);
          return;
        }
        const list = Array.isArray(data.data) ? data.data : [];
        setContracts(list);
        setHasMore(list.length === LIMIT);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Failed to load contracts. Please try again.");
        setContracts([]);
        setHasMore(false);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [currentPage, debouncedSearch, statusFilter, apiRole, refreshKey]);

  // ── Status filter change resets to page 1 ─────────────────────
  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  // ── Navigation ─────────────────────────────────────────────────
  const goToDetails = (contract) => {
    history.push(`/escrow/${role}/contract/${contract.contractNo}`);
  };

  const goToFund = (contract) => {
    history.push(`/escrow/contract/fund/${contract.contractNo}`);
  };

  const goToEdit = (contract) => {
    history.push(`/escrow/contract/create/${contract.contractNo}`);
  };

  const handleWithdraw = (contract) => {
    history.push("/wallet");
  };

  // ── Confirm configs ────────────────────────────────────────────
  const CONFIRM_CONFIGS = {
    fund: {
      title: "Fund Escrow",
      message: (c) => `You are about to deposit funds into escrow for "${c.contractTitle}". This action cannot be undone once the payee begins work.`,
      confirmLabel: "Fund Escrow",
      danger: false,
    },
    dispute: {
      title: "Open Dispute",
      message: (c) => `Opening a dispute for "${c.contractTitle}" will freeze the funds and notify our resolution team. Are you sure?`,
      confirmLabel: "Open Dispute",
      danger: false,
    },
    cancel: {
      title: "Cancel Contract",
      message: (c) => `Are you sure you want to cancel "${c.contractTitle}"?`,
      confirmLabel: "Cancel Contract",
      danger: true,
    },
    request_review: {
      title: "Request Review",
      message: (c) => `Submit a review request for "${c.contractTitle}"? The payer will be notified to evaluate and release the escrow funds.`,
      confirmLabel: "Submit Request",
      danger: false,
    },
    respond: {
      title: "Respond to Dispute",
      message: (c) => `You are about to submit a response to the active dispute on "${c.contractTitle}". Make sure your evidence is ready before proceeding.`,
      confirmLabel: "Submit Response",
      danger: false,
    },
    approve: {
      title: "Approve & Release Funds",
      message: (c) => `Approving "${c.contractTitle}" will release the escrowed funds to the payee. This action is irreversible.`,
      confirmLabel: "Approve",
      danger: false,
    },
    reject: {
      title: "Reject Contract",
      message: (c) => `Rejecting "${c.contractTitle}" will return the escrowed funds to the payer. The payee will be notified.`,
      confirmLabel: "Reject",
      danger: true,
    },
  };

  const activeConfig = confirm
    ? {
        ...CONFIRM_CONFIGS[confirm.type],
        message: CONFIRM_CONFIGS[confirm.type]?.message(confirm.contract),
      }
    : {};

  const handleConfirm = () => {
    if (!confirm) return;
    const { type, contract } = confirm;
    const update = (patch) =>
      setContracts((prev) =>
        prev.map((c) => (c.contractId === contract.contractId ? { ...c, ...patch } : c))
      );
    if (type === "fund")           update({ status: "ACTIVE" });
    else if (type === "dispute")   update({ status: "IN_DISPUTE" });
    else if (type === "cancel")    update({ status: "CANCELLED" });
    else if (type === "approve")   update({ status: "COMPLETED" });
    else if (type === "reject")    update({ status: "CANCELLED" });
    setConfirm(null);
  };

  // ── Row rendering ──────────────────────────────────────────────
  const renderRow = (contract) => {
    const payees  = getParticipantsByRole(contract.participants, "PAYEE");
    const payers  = getParticipantsByRole(contract.participants, "PAYER");

    const sharedCols = (
      <>
        <td>
          <div className="ec-td-name">{contract.contractTitle || "—"}</div>
          <div className="ec-td-sub">{contract.contractNo}</div>
        </td>
        <td>
          <span className="ec-td-amount">
            {fmtAmount(contract.totalContractAmount)}
            {contract.fundedCurrency ? ` ${contract.fundedCurrency}` : ""}
          </span>
        </td>
      </>
    );

    if (role === "payer") {
      return (
        <tr key={contract.contractId}>
          {sharedCols}
          <td><ParticipantList participants={payees} /></td>
          <td><StatusBadge status={contract.status} /></td>
          <td>{contract.fundingStatus ? <StatusBadge status={contract.fundingStatus} /> : <span style={{ opacity: 0.35 }}>—</span>}</td>
          <td style={{ color: "var(--text-color)", opacity: 0.75 }}>{fmtDate(contract.createdAt)}</td>
          <td>
            <PayerActions
              contract={contract}
              merchantId={merchantId}
              onView={goToDetails}
              onFund={goToFund}
              onEdit={goToEdit}
              onCancel={(c) => setCancelModal({ contract: c })}
            />
          </td>
        </tr>
      );
    }

    if (role === "payee") {
      return (
        <tr key={contract.contractId}>
          {sharedCols}
          <td><ParticipantList participants={payers} /></td>
          <td><StatusBadge status={contract.status} /></td>
          <td>{contract.fundingStatus ? <StatusBadge status={contract.fundingStatus} /> : <span style={{ opacity: 0.35 }}>—</span>}</td>
          <td style={{ color: "var(--text-color)", opacity: 0.75 }}>{fmtDate(contract.createdAt)}</td>
          <td>
            <PayeeActions
              contract={contract}
              merchantId={merchantId}
              onView={goToDetails}
              onEdit={goToEdit}
              onDispute={(c) => setDisputeModal({ contract: c })}
              onWithdraw={handleWithdraw}
            />
          </td>
        </tr>
      );
    }

    // approval
    return (
      <tr key={contract.contractId}>
        {sharedCols}
        <td><ParticipantList participants={payers} /></td>
        <td><ParticipantList participants={payees} /></td>
        <td><StatusBadge status={contract.status} /></td>
        <td>{contract.fundingStatus ? <StatusBadge status={contract.fundingStatus} /> : <span style={{ opacity: 0.35 }}>—</span>}</td>
        <td>
          <ApprovalActions
            contract={contract}
            merchantId={merchantId}
            onReview={goToDetails}
            onEdit={goToEdit}
            onApprove={(c) => setConfirm({ type: "approve", contract: c })}
            onReject={(c) => setConfirm({ type: "reject", contract: c })}
          />
        </td>
      </tr>
    );
  };

  const colSpan = role === "approval" ? 7 : 7;

  // ─────────────────────────────────────────────────────────────
  return (
    <Fragment>
      <style>{CSS}</style>
      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div className="ec-page ec-view-in">

            {/* Header */}
            <div className="ec-hdr">
              <div className="ec-hdr-left">
                <h1 className="ec-title">{title}</h1>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                  <p className="ec-sub">{sub}</p>
                  <span className={`ec-role-chip ${role}`}>
                    <i className={`fas ${ROLE_ICONS[role]}`} style={{ fontSize: 10 }} />
                    {ROLE_LABELS[role]}
                  </span>
                </div>
              </div>
              {role === "payer" && (
                <div className="ec-hdr-right">
                  <button
                    className="ec-btn-create"
                    onClick={() => history.push("/escrow/contract/create")}
                  >
                    <i className="fas fa-plus" style={{ fontSize: 12 }} />
                    Create Contract
                  </button>
                </div>
              )}
            </div>

            {/* Top bar */}
            <div className="ec-topbar">
              <div className="ec-search-wrap">
                <span className="ec-search-icon">
                  <i className="fas fa-search" style={{ fontSize: 13 }} />
                </span>
                <input
                  className="ec-search-input"
                  placeholder="Search by contract ID or title…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <select
                className="ec-filter-sel"
                value={statusFilter}
                onChange={handleStatusChange}
              >
                <option value="ALL">All Statuses</option>
                <option value="SAVE_DRAFT">Draft</option>
                <option value="CREATED">Created</option>
                <option value="PENDING">Pending</option>
                <option value="FUNDED">Funded</option>
                <option value="PARTIALLY_FUNDED">Partially Funded</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="DISPUTED">Disputed</option>
                <option value="IN_REVIEW">In Review</option>
                <option value="RELEASE_APPROVED">Release Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="FUNDING_REJECTED">Fund Rejected</option>
              </select>
            </div>

            {/* Table */}
            <div className="ec-tbl-wrap">
              <table>
                <TableHead role={role} />
                <tbody>
                  {loading ? (
                    <SkeletonRows colSpan={colSpan} />
                  ) : error ? (
                    <tr>
                      <td colSpan={colSpan}>
                        <div className="ec-error">
                          <div className="ec-error-icon">
                            <i className="fas fa-exclamation-circle" />
                          </div>
                          <p className="ec-error-text">{error}</p>
                          <button
                            className="ec-retry-btn"
                            onClick={() => setCurrentPage((p) => p)}
                          >
                            <i className="fas fa-redo" style={{ fontSize: 11, marginRight: 5 }} />
                            Retry
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : contracts.length === 0 ? (
                    <tr>
                      <td colSpan={colSpan}>
                        <div className="ec-empty">
                          <div className="ec-empty-icon">
                            <i className="fas fa-file-contract" />
                          </div>
                          <p className="ec-empty-text">No contracts found</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    contracts.map(renderRow)
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!error && (
              <Pagination
                currentPage={currentPage}
                hasMore={hasMore}
                loading={loading}
                onPageChange={setCurrentPage}
              />
            )}

          </div>
          <Footer />
        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={!!confirm}
        title={activeConfig.title}
        message={activeConfig.message}
        confirmLabel={activeConfig.confirmLabel}
        danger={activeConfig.danger}
        onConfirm={handleConfirm}
        onCancel={() => setConfirm(null)}
      />

      {/* Cancel Modal */}
      <CancelModal
        open={!!cancelModal}
        contract={cancelModal?.contract || null}
        merchantId={merchantId}
        onClose={() => setCancelModal(null)}
        onSuccess={() => setRefreshKey((k) => k + 1)}
      />

      {/* Dispute Modal */}
      <DisputeModal
        open={!!disputeModal}
        contractNumericId={disputeModal?.contract?.contractId || null}
        raisedBy={parseInt(merchantId, 10) || 0}
        contractTitle={disputeModal?.contract?.contractTitle || ""}
        onClose={() => setDisputeModal(null)}
        onSuccess={() => setRefreshKey((k) => k + 1)}
      />
    </Fragment>
  );
}
